import type * as React from "react";
import { useId, useState } from "react";
import { Button, type ButtonProps } from "../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/dialog";
import { Label } from "../components/label";
import { Textarea } from "../components/textarea";

export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Called with the trimmed reason; the dialog closes when the promise resolves. */
  onConfirm: (reason: string) => unknown;
  confirmLabel?: string;
  confirmVariant?: ButtonProps["variant"];
  reasonLabel?: string;
  placeholder?: string;
  /** Minimum reason length; 0 makes the reason optional. */
  minLength?: number;
  /** Extra fields rendered above the reason textarea. */
  children?: React.ReactNode;
}

/**
 * Confirmation dialog that requires a written reason (audit trail for destructive /
 * security actions: revoke, remove, deactivate, plan change).
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "Confirm",
  confirmVariant = "destructive",
  reasonLabel = "Reason",
  placeholder = "Why is this being done? Recorded in the audit log.",
  minLength = 3,
  children,
}: ReasonDialogProps) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const invalid = trimmed.length < minLength;

  const close = (next: boolean) => {
    if (busy) return;
    if (!next) {
      setReason("");
      setTouched(false);
    }
    onOpenChange(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (invalid) return;
    setBusy(true);
    try {
      await onConfirm(trimmed);
      setReason("");
      setTouched(false);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          {children}
          <div>
            <Label htmlFor={id}>
              {reasonLabel}
              {minLength > 0 ? " (required)" : ""}
            </Label>
            <Textarea
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={placeholder}
              aria-invalid={touched && invalid ? true : undefined}
              autoFocus
            />
            {touched && invalid ? (
              <p className="mt-1 text-destructive text-xs">
                Please give a reason (at least {minLength} characters).
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant={confirmVariant} disabled={busy}>
              {busy ? "Working…" : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
